import * as React from "react";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ImagesContext } from "../Context";
import InputMask from "react-input-mask";
import { User } from "./ProviderEdit";
import UnsavedDataConfirmation from "../modals/UnsavedDataConfirmation";

// --- Validation Helper Imports ---
import {
  MAX_NAME_LENGTH,
  NAME_REGEX,
  getNameError,

  MAX_PHONE_LENGTH,
  getPhoneNumberError,

  MAX_EMAIL_LENGTH,
  EMAIL_REGEX,
  getEmailError,
} from "../utils/DemographicValidationHelper";


interface Props {
  user: User;
  unsavedChanges: any;
  setUnsavedChanges: Function;
  customers: any[];
  handleUpdateUser: any;
  handleChange: <K extends keyof User>(field: K, value: User[K]) => void;
  resetUserInfo: any;
  expanded: boolean;
  setExpanded: any;
}

// Normalize stored value into plain digits before giving it to InputMask

const AccountInformation: React.FC<Props> = ({
  user,
  unsavedChanges,
  setUnsavedChanges,
  customers,
  handleUpdateUser,
  handleChange,
  resetUserInfo,
  expanded,
  setExpanded,
}) => {
  const images = React.useContext(ImagesContext);

  const [showUnsavedModal, setShowUnsavedModal] = React.useState(false);

  

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleCancel = () => {
    if (unsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      setExpanded(false);
    }
  };
  const normalizedPhone = String(user.mobilePhone || "")
    .replace(/\D/g, "")
    .slice(0, 10); // Phone number is 10 digits

  const handleModalConfirm = () => {
    resetUserInfo();
    setUnsavedChanges(false);
    setExpanded(false);
    setShowUnsavedModal(false);
  };

  const handleModalClose = () => {
    setShowUnsavedModal(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "4px",
      }}
    >
      <Box
        className="profile-list-tile"
        sx={{
          boxShadow: expanded && "0px 7px 7px 0px #00000040",
        }}
      >
        <Box
          sx={{
            display: "flex",
            columnGap: "16px",
            alignItems: "center",
          }}
        >
          <div className="item-icon-container">
            <img
              src={images.manage_account_icon}
              alt="Insurance Icon"
              className="action-icon-image"
              style={{ width: 23, maxHeight: 23 }}
            />
          </div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "10px",
            }}
          >
            <Box display="flex" alignItems="center" columnGap="16px">
              <Typography
                sx={{
                  fontSize: "18px",
                  lineHeight: "18px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  color: "#4A4442",
                }}
              >
                Account Information
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "18px",
                fontWeight: 500,
                letterSpacing: "1px",
                color: "#707070",
              }}
            >
              Update your account’s general details
            </Typography>
          </Box>
        </Box>

        {expanded ? (
          <Box className="row">
            <Link
              style={{
                marginRight: "16px",
                marginLeft: "32px",
                color: "black",
              }}
              onClick={handleCancel}
            >
              Cancel
            </Link>
            <Link className="save-btn" onClick={handleUpdateUser}>
              Save
            </Link>
          </Box>
        ) : (
          <Button className="expand-button" onClick={handleExpand}>
            Manage
          </Button>
        )}
      </Box>

      {expanded && (
        <Box className="edit-container">
          <Grid item xs={12} className="profile-edit-container">
            <Grid container>
              <Grid
                container
                className="profile-form-container"
                columnSpacing={3}
              >
                <Grid item xs={2.5}>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="first_name" className="field-label">
                        First Name*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        id="first_name"
                        size="small"
                        value={user.firstName}
                        className="the-field"
                        required
                        variant="outlined"
                        inputProps={{
                          maxLength: MAX_NAME_LENGTH,
                          pattern: NAME_REGEX.source
                        }}
                        error={Boolean(getNameError(user.firstName, "First name"))}
                        helperText={getNameError(user.firstName, "First name")}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const trimmedValue = raw.replace(/^\s+/, '');
                          if (trimmedValue.length > MAX_NAME_LENGTH) return;
                          if (trimmedValue && !NAME_REGEX.test(trimmedValue)) return;
                          setUnsavedChanges(true);
                          handleChange("firstName", trimmedValue);
                        }}
                        onKeyPress={() => {
                          setUnsavedChanges(true);
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="email" className="field-label">
                        Email*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        id="email"
                        size="small"
                        value={user.email}
                        className="the-field"
                        required
                        variant="outlined"
                        inputProps={{
                          maxLength: MAX_EMAIL_LENGTH,
                          pattern: EMAIL_REGEX.source
                        }}
                        error={Boolean(getEmailError(user.email))}
                        helperText={getEmailError(user.email)}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const trimmedValue = raw.replace(/^\s+/, '');
                          if (trimmedValue.length > MAX_EMAIL_LENGTH) return;
                          if (trimmedValue && !EMAIL_REGEX.test(trimmedValue)) {
                            if (/[\s]/.test(trimmedValue)) return;
                          }
                          setUnsavedChanges(true);
                          handleChange("email", trimmedValue);
                        }}
                        onKeyPress={() => {
                          setUnsavedChanges(true);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={2.5}>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="middle_name" className="field-label">
                        Middle Name
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        id="middle_name"
                        size="small"
                        value={user.middleName}
                        required
                        variant="outlined"
                        className="the-field"
                        inputProps={{
                          maxLength: MAX_NAME_LENGTH,
                          pattern: NAME_REGEX.source
                        }}
                        error={Boolean(getNameError(user.middleName, "Middle name"))}
                        helperText={getNameError(user.middleName, "Middle name")}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const trimmedValue = raw.replace(/^\s+/, '');
                          if (trimmedValue.length > MAX_NAME_LENGTH) return;
                          if (trimmedValue && !NAME_REGEX.test(trimmedValue)) return;
                          setUnsavedChanges(true);
                          handleChange("middleName", trimmedValue);
                        }}
                        onKeyPress={() => {
                          setUnsavedChanges(true);
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel
                        htmlFor="mobile_phone_number"
                        className="field-label"
                      >
                        Mobile Phone*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <InputMask
                        mask="(999) 999-9999"
                        value={normalizedPhone}
                        maskChar=""
                        onChange={(event) => {
                          const raw = event.target.value;
                          const digits = raw.replace(/\D/g, "");

                          if (!/^\d*$/.test(digits)) return;
                          if (digits.length > 10) return;

                          setUnsavedChanges(true);
                          handleChange("mobilePhone", digits);
                        }}
                      >
                        {(inputProps: any) => {
                          const digits = normalizedPhone;
                          const phoneError = getPhoneNumberError(digits); 

                          return (
                            <TextField
                              {...inputProps} 
                              variant="outlined"
                              size="small"
                              className="the-field"
                              error={phoneError.error}
                              helperText={phoneError.helperText}
                              inputProps={{
                                ...inputProps?.inputProps, 
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                maxLength: 14,
                              }}
                            />
                          );
                        }}
                      </InputMask>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={2.5}>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="last_name" className="field-label">
                        Last Name*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        id="last_name"
                        size="small"
                        value={user.lastName}
                        required
                        variant="outlined"
                        className="the-field"
                        inputProps={{
                          maxLength: MAX_NAME_LENGTH,
                          pattern: NAME_REGEX.source
                        }}
                        error={Boolean(getNameError(user.lastName, "Last name", true))}
                        helperText={getNameError(user.lastName, "Last name", true)}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const trimmedValue = raw.replace(/^\s+/, '');
                          if (trimmedValue && !NAME_REGEX.test(trimmedValue)) return;
                          if (trimmedValue.length > MAX_NAME_LENGTH) return;

                          setUnsavedChanges(true);
                          handleChange("lastName", trimmedValue);
                        }}
                        onKeyPress={() => setUnsavedChanges(true)}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="customer_id" className="field-label">
                        Default Organization Login*
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} className="field-container">
                      <TextField
                        id="customer_id"
                        name="customer[customer_id]"
                        className="the-field"
                        value={user.customer}
                        size="small"
                        variant="outlined"
                        onChange={(event) =>
                          handleChange("customer", event.target.value)
                        }
                        select
                      >
                        {customers.map((customer, index) => (
                          <MenuItem key={index + 1} value={customer.id}>
                            {customer.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}

      <UnsavedDataConfirmation
        open={showUnsavedModal}
        closeModal={handleModalClose}
        handleConfirm={handleModalConfirm}
      />
    </Box>
  );
};

export default AccountInformation;
